import styled from "@emotion/styled";
import { Drawer } from "antd";
import {
  AUTH_API_URL,
  ModelType,
  PROJECT_CLIENT_ID,
  sendMessageToParent,
  StorageMobile,
  useProjectModal,
} from "./util";
import { DisplayText } from "components/lib";
import { JoinMemberForm } from "../forms/join-member-form";
import { VerifyPhoneForm } from "../forms/verify-phone-form";
import { LoginForm } from "../forms/login-form";
import { LabelType } from "./page-header";
import { useEffect, useMemo, useState } from "react";
import closeIcon from "assets/closeIcon.png";
import parsePhoneNumberFromString from "libphonenumber-js";
import "./customize.css";
import {
  MIX_PANEL_EVENT,
  trackMixPanel,
  useTrackCommonData,
} from "mixpanel/mixpanelTrackUtil";
import { useWindowSize } from "utils";
import { useUrlQueryParam } from "utils/url";
import "@fantuaneats/user-common/dist/index.css";
import { UserConsent } from "@fantuaneats/user-common";
import { UserDialog } from "@fantuaneats/user-common";

const DEFAULT_STATUS = 3;
export const PageDrawerJoin = (props: { lang: LabelType, enableRegister?: boolean, onboardingForTwilio?: boolean }) => {
  const { enableRegister, onboardingForTwilio } = useMemo(() => {
    const enableRegister = props.enableRegister === undefined ? true : props.enableRegister;
    const onboardingForTwilio = props.onboardingForTwilio === undefined ? false : props.onboardingForTwilio;
    return {
      enableRegister: enableRegister || onboardingForTwilio,
      onboardingForTwilio,
    }

  }, [props.enableRegister, props.onboardingForTwilio]);
  const drawTypeConfig = useMemo(
    () => ({
      join: { zh: "加入会员", en: "Member Sign-up", height: onboardingForTwilio ? "36.5rem" : "46rem" }, // 24.4rem
      verify: {
        zh: "验证手机号",
        en: "Verify Phone Number ",
        height: "21.8rem",
      }, // 21.8rem
      login: { zh: "登录会员", en: "Login", height: "21.8rem" }, // 21.8rem
      codeTip: {
        zh: "已发送验证码到手机 ",
        en: "The code has been sent to ",
      },
      verifyAfter: {
        zh: { join: "加入会员", login: "登录会员" },
        en: { join: "Join", login: "Login" },
      },
    }),
    [onboardingForTwilio]
  );

  const { projectModalOpen, close, modelType, originFrom } = useProjectModal();
  const [drawHeight, setDrawHeight] = useState("");
  const trackCommonData = useTrackCommonData();

  const { width: currentWidth } = useWindowSize();
  const [multiple, setMultiple] = useState(10);

  const [{ source = "" }] = useUrlQueryParam(["source"]);

  const [userStatus, setUserStatus] = useState(DEFAULT_STATUS);
  const [userConVisible, setUserConVisible] = useState(false);
  const [refreshNum, setRefreshNum] = useState(0);
  const [phoneBraze, setPhoneBraze] = useState("");

  useEffect(() => {
    if (currentWidth > 600) {
      setMultiple(16);
    } else if (currentWidth < 340) {
      setMultiple(8);
    } else {
      setMultiple(10);
    }
  }, [currentWidth]);

  useEffect(() => {
    if (!projectModalOpen) {
      return;
    }
    if (drawHeight) {
      const val = (Math.floor(parseFloat(drawHeight) * 1000) / 1000) * multiple;
      sendMessageToParent({ type: "height", data: val + "px" }, source);
    }
  }, [drawHeight, multiple, source, projectModalOpen]);

  // 没电话号码，关闭弹框，流程重新启动
  const originPhone = StorageMobile("get") || "";
  useEffect(() => {
    if (!originPhone && projectModalOpen && modelType === "verify") {
      close();
    }
  });

  useEffect(() => {
    setDrawHeight(drawTypeConfig[modelType as ModelType]?.height);
  }, [modelType, drawTypeConfig]);

  const [verifyPhone, setVerifyPhone] = useState(
    parsePhoneNumberFromString(originPhone, {
      defaultCountry: "US",
    })?.formatNational()
  );

  const setPhoneData = (val: string) => {
    setVerifyPhone(val);
  };

  const handleClose = (e: any) => {
    setUserStatus(DEFAULT_STATUS);
    setUserConVisible(false);
    setRefreshNum(0);
    sendMessageToParent({ type: "closeDialog", data: null }, source);
    switch (modelType) {
      case "join":
        trackMixPanel(
          MIX_PANEL_EVENT.REGISTER_CLOSE_NAME_PHONE,
          trackCommonData
        );
        break;
      case "verify":
        if (originFrom === "register") {
          trackMixPanel(
            MIX_PANEL_EVENT.REGISTER_CLOSE_VERIFY_CODE,
            trackCommonData
          );
        } else if (originFrom === "login") {
          trackMixPanel(
            MIX_PANEL_EVENT.LOGIN_CLOSE_VERIFY_CODE,
            trackCommonData
          );
        }
        break;
      case "login":
        trackMixPanel(MIX_PANEL_EVENT.LOGIN_CLOSE_NAME_PHONE, trackCommonData);
        break;
      default:
        break;
    }
    close();
  };

  const checkBrazeFailed = (val: string) => {
    setPhoneBraze(val);
    setUserStatus(2);
  };

  const confirmNext = () => {
    trackMixPanel(MIX_PANEL_EVENT.USER_CONSENT_AGREE, trackCommonData);
    trackMixPanel(MIX_PANEL_EVENT.USER_CONSENT_CHECKSTATUS, {
      checkStatus: true,
      ...trackCommonData,
    });
    setRefreshNum((val) => val + 1);
  };

  const closeUserDialog = (b: boolean) => {
    trackMixPanel(MIX_PANEL_EVENT.USER_CONSENT_CLOSE, trackCommonData);
    setUserConVisible(b);
  };

  useEffect(() => {
    if (userStatus === 0) {
      trackMixPanel(MIX_PANEL_EVENT.USER_CONSENT_CHECKSTATUS, {
        checkStatus: false,
        ...trackCommonData,
      });
      trackMixPanel(MIX_PANEL_EVENT.USER_CONSENT_OPEN, {
        ...trackCommonData,
      });
      setUserConVisible(true);
    } else if (userStatus === 1) {
      trackMixPanel(MIX_PANEL_EVENT.USER_CONSENT_CHECKSTATUS, {
        checkStatus: true,
        ...trackCommonData,
      });
      setRefreshNum((val) => val + 1);
    }
  }, [userStatus, trackCommonData]);

  if (!enableRegister && modelType === "join") {
    return null;
  }
  return (
    // forceRender={true} 解决useForm无元素挂载的问题
    <>
      <Drawer
        forceRender={true}
        visible={projectModalOpen && !userConVisible}
        placement={"bottom"}
        width={"100vw"}
        // 初始是 24.4 和
        height={drawHeight}
        closable={false}
        style={{ padding: 0, borderRadius: "1.2rem 1.2rem 029re 0" }}
        bodyStyle={{ padding: 0 }}
        className={"drawClasses"}
      >
        <DrawTitle>
          {source !== "smart_ordering" && (
            <CloseOutlinedBox src={closeIcon} onClick={handleClose} />
          )}
          <span style={{ fontSize: "1.8rem" }}>
            {userConVisible
              ? "Chowbus"
              : drawTypeConfig[modelType as ModelType]?.[props.lang]}
          </span>
        </DrawTitle>
        {modelType === "verify" && (
          <CodeTip>
            {drawTypeConfig.codeTip[props.lang]}+1 {verifyPhone}
          </CodeTip>
        )}
        {modelType === "join" && enableRegister && (
          <JoinMemberForm
            setPhone={setPhoneData}
            setHeight={setDrawHeight}
            userStatus={userStatus}
            userConVisible={userConVisible}
            checkedFailed={checkBrazeFailed}
            refreshNum={refreshNum}
            onboardingForTwilio={onboardingForTwilio}
            onboardingForTwilioCloseCallback={handleClose}
          >
            {
              !onboardingForTwilio && <UserConsent
                lang={props.lang}
                userStatus={userStatus}
                setCheckStatus={(status: number) => setUserStatus(status)}
                apiLink={AUTH_API_URL}
                apiConfig={{
                  method: "get",
                  headers: {
                    "client-id": PROJECT_CLIENT_ID,
                  },
                  data: {
                    phone_number: phoneBraze,
                  },
                }}
              ></UserConsent>
            }

          </JoinMemberForm>
        )}
        {modelType === "verify" && (
          <VerifyPhoneForm setHeight={setDrawHeight} />
        )}
        {modelType === "login" && (
          <LoginForm setPhone={setPhoneData} setHeight={setDrawHeight} />
        )}
      </Drawer>
      <UserDialog
        userConVisible={userConVisible}
        setUserConVisible={closeUserDialog}
        setUserStatus={setUserStatus}
        confirmNext={confirmNext}
        lang={props.lang}
        contentStyle={{
          height: props.lang === "en" ? "33rem" : "25rem",
          content: { padding: "0 1.6rem" },
        }}
      ></UserDialog>
    </>
  );
};

export const DrawTitle = styled(DisplayText)`
  font-weight: 600;
  font-size: 1.8rem;
  line-height: 2.5rem;
  height: 4.4rem;
  position: relative;
  margin-bottom: 1.2rem;
`;

const CodeTip = styled.div`
  text-align: center;
  color: #92929f;
  font-size: 1.4rem;
  line-height: 1;
  margin-bottom: 1.6rem;
`;

export const CloseOutlinedBox = styled.img`
  position: absolute;
  left: 0;
  top: 0;
  width: 4.4rem;
  height: 4.4rem;
`;
